import { atom } from 'jotai';
import {
  Company as ApiCompany,
  PortalUser as ApiPortalUser,
  PortalLoginStatusResponse,
  PortalAuthenticationResponse,
  Country,
  Language,
  PortalTab,
  SentAccess,
  ServiceType,
  EidProvider,
} from '@idk-web/api';
import { store } from '@/state/store';

export type PortalUser = {
  id: number;
  username: string;
  displayName: string;
  email: string | null;
  country: Country | null;
  mustChangePassword: boolean;
  hasValidatedEmail: boolean;
};

export type CountryConfiguration = {
  teleidEnabled: boolean;
  teleidSsnEnabled: boolean;
  teleidEmailEnabled: boolean;
  teleidSmsEnabled: boolean;
  teleidChatEnabled: boolean;
  teleidMaxUsersNumber: number;
  teleidCounter: number;
  telesignEnabled: boolean;
  telesignSsnEnabled: boolean;
  telesignEmailEnabled: boolean;
  telesignSmsEnabled: boolean;
  telesignChatEnabled: boolean;
  telesignCounter: number;
  telesignMaxUsersNumber: number;
  safemailEnabled: boolean;
  safemailEmailEnabled: boolean;
  safemailSmsEnabled: boolean;
  safemailCounter: number;
  safemailMaxUsersNumber: number;
  provider: EidProvider;
  country: Country;
  defaultLanguage?: Language;
};

export type AvailableCompany = {
  id: number;
  code: string;
  name: string;
  isAdmin: boolean;
};

export type Company = {
  id: number;
  code: string;
  name: string;
  isAdmin: boolean;
  services: {
    type: ServiceType;
    countries: Country[];
    providers: EidProvider[];
  }[];
  countries: CountryConfiguration[];
  defaultStartTab: PortalTab | null;
  defaultBankIdCountry: Country | null;
  logAccess: boolean;
  telesignDefaultText: string | null;
  telesignDefaultTextDisabled: boolean;
  formIdOnlySafemail: boolean;
  forms: {
    id: number;
    name: string;
    path: string;
    email: string;
    autodeleteCountDays: number;
    providers: EidProvider[];
  }[];
  requestSignatureEnabled: boolean;
  safemailRepliesEnabled: boolean;
  safemailSignRequiresAttachment: boolean;
  safemailMultipleAttachments: boolean;
  autodeleteCountDays: number;
  companyLogoUrl: string | null;
  apiEnabled: boolean;
  apiClientIp: string[];
  sentAccess: SentAccess;
  timezone: string | null;
  usernamePasswordNewAccountsDisabled: boolean;
  companySignatoryEnabled: boolean;
  sparEnabled: boolean;
  apiKey: string | null;
  apiToken: string | null;
};

export type PortalAuthState = {
  sso?: {
    registrationId: string;
  };
} & (
  | {
      status: 'LOGGED_OUT';
      id: null;
      token: null;
      user: null;
      currentCompany: null;
      availableCompanies: [];
    }
  | {
      status: 'LOGGED_IN';
      id: string;
      token: null;
      user: PortalUser;
      currentCompany: null;
      availableCompanies: AvailableCompany[];
    }
  | {
      status: 'AUTHENTICATED';
      id: string;
      token: string;
      user: PortalUser;
      currentCompany: Company;
      availableCompanies: AvailableCompany[];
    }
);

const initialState: PortalAuthState = {
  status: 'LOGGED_OUT',
  id: null,
  token: null,
  user: null,
  currentCompany: null,
  availableCompanies: [],
  sso: undefined,
};

export const portalAuthState = atom<PortalAuthState>(initialState);

export const portalLoginStatus = atom((get) => get(portalAuthState).status);

export const portalToken = atom((get) => get(portalAuthState).token);

export const portalUser = atom((get) => get(portalAuthState).user);

export const portalCompany = atom((get) => get(portalAuthState).currentCompany);

export const availablePortalCompanies = atom((get) => {
  const state = get(portalAuthState);

  return state.availableCompanies.filter(
    (company) => company.id !== state.currentCompany?.id,
  );
});

export const portalSso = atom((get) => get(portalAuthState).sso);

export function setPortalAuthState(state: PortalAuthState): void {
  store.set(portalAuthState, state);
}

export function logout(): void {
  setPortalAuthState({ ...initialState, sso: store.get(portalAuthState).sso });
}

export function clearState(): void {
  setPortalAuthState(initialState);
}

export function login(
  payload: Extract<PortalLoginStatusResponse, { status: 'COMPLETED' }>,
): void {
  setPortalAuthState({
    status: 'LOGGED_IN',
    id: payload.id,
    token: null,
    user: {
      id: payload.user.id,
      username: payload.user.username,
      displayName: payload.user.displayName ?? '',
      email: payload.user.email,
      country: payload.user.country,
      mustChangePassword: payload.mustChangePassword,
      hasValidatedEmail: payload.hasValidatedEmail,
    },
    currentCompany: null,
    availableCompanies: payload.companies.map((company) => ({
      id: company.companyId,
      code: company.companyCode,
      name: company.companyName,
      isAdmin: company.isAdmin,
    })),
    sso: payload.sso,
  });
}

export function updateUser(user: ApiPortalUser): void {
  const state = store.get(portalAuthState);

  if (state.status === 'LOGGED_OUT') {
    return;
  }

  setPortalAuthState({
    ...state,
    user: {
      ...state.user,
      displayName: user.displayName ?? '',
      email: user.email,
      mustChangePassword: user.mustChangePassword,
      hasValidatedEmail: user.hasValidatedEmail,
    },
  });
}

export function authenticate(payload: {
  auth: PortalAuthenticationResponse;
  config: ApiCompany;
}): void {
  const { auth, config } = payload;
  const state = store.get(portalAuthState);

  if (state.status === 'LOGGED_OUT') {
    return;
  }

  setPortalAuthState({
    status: 'AUTHENTICATED',
    id: state.id,
    token: auth.token,
    user: state.user,
    currentCompany: {
      id: config.id,
      code: config.code,
      name: auth.company.name,
      isAdmin: auth.user.isAdmin,
      services: auth.user.availableServices,
      countries: config.configurations.map((config) => ({
        teleidEnabled: config.teleidEnabled,
        teleidSsnEnabled: config.teleidSsnEnabled,
        teleidEmailEnabled: config.teleidEmailEnabled,
        teleidSmsEnabled: config.teleidSmsEnabled,
        teleidChatEnabled: config.teleidChatEnabled,
        teleidMaxUsersNumber: config.teleidMaxUsersNumber,
        teleidCounter: config.teleidCounter,
        telesignEnabled: config.telesignEnabled,
        telesignSsnEnabled: config.telesignSsnEnabled,
        telesignEmailEnabled: config.telesignEmailEnabled,
        telesignSmsEnabled: config.telesignSmsEnabled,
        telesignChatEnabled: config.telesignChatEnabled,
        telesignCounter: config.telesignCounter,
        telesignMaxUsersNumber: config.telesignMaxUsersNumber,
        safemailEnabled: config.safemailEnabled,
        safemailEmailEnabled: config.safemailEmailEnabled,
        safemailSmsEnabled: config.safemailSmsEnabled,
        safemailCounter: config.safemailCounter,
        safemailMaxUsersNumber: config.safemailMaxUsersNumber,
        provider: config.provider,
        country: config.country,
        defaultLanguage: config.defaultLanguage,
      })),
      defaultStartTab: auth.company.defaultStartTab,
      defaultBankIdCountry: auth.company.defaultCountry,
      logAccess: auth.user.canSeeLogs,
      telesignDefaultText: auth.company.telesignDefaultText,
      telesignDefaultTextDisabled: auth.company.telesignDefaultTextDisabled,
      formIdOnlySafemail: auth.company.formIdOnlySafemail,
      forms: config.forms.map((form) => ({
        id: form.id,
        name: form.name,
        path: form.path,
        email: form.email,
        autodeleteCountDays: form.autodeleteCountDays ?? 180,
        providers: form.providers,
      })),
      requestSignatureEnabled: auth.company.requestSignatureEnabled,
      safemailRepliesEnabled: config.safemailRepliesEnabled,
      safemailSignRequiresAttachment:
        auth.company.safemailSignRequiresAttachment,
      safemailMultipleAttachments: auth.company.safemailMultipleAttachments,
      autodeleteCountDays: config.autodeleteCountDays,
      companyLogoUrl: auth.company.logoUrl,
      apiEnabled: config.apiEnabled,
      apiClientIp: config.apiClientIp?.split(',') ?? [],
      sentAccess: config.sentAccess,
      timezone: config.timezone,
      usernamePasswordNewAccountsDisabled:
        config.usernamePasswordNewAccountsDisabled,
      companySignatoryEnabled: config.companysignatory,
      sparEnabled: config.spar,
      apiKey: config.apiKey,
      apiToken: config.apiToken,
    },
    availableCompanies: state.availableCompanies,
    sso: state.sso,
  });
}

export default portalAuthState;
